import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import {
  Palette,
  Upload,
  Trash2,
  Image as ImageIcon,
  Type,
  Monitor,
  Globe,
  Check,
  Mail
} from 'lucide-react'
import toast from 'react-hot-toast'
import { HUB_BRANDING_QUERY, hubBrandingAPI, useHubBranding } from '../hooks/useHubBranding'
import EmailBrandingTab from '../components/Branding/EmailBrandingTab'
import enginuoLogo from '../assets/images/enginuo-logo.png'

const DEFAULT_PRIMARY = '#3B82F6'
const DEFAULT_SECONDARY = '#64748B'
const HEX = /^#([0-9A-Fa-f]{6})$/
const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif,image/x-icon,.ico'
const MAX_BYTES = 5 * 1024 * 1024

const normalizeHex = (value, fallback) => {
  if (!value) return fallback
  const next = value.startsWith('#') ? value : `#${value}`
  return HEX.test(next) ? next.toUpperCase() : fallback
}

function DropZone({ label, hint, previewUrl, file, compact = false, onFile, onClear }) {
  const [dragOver, setDragOver] = useState(false)

  const takeFile = (picked) => {
    if (!picked) return
    if (picked.size > MAX_BYTES) {
      toast.error('Image must be 5MB or smaller')
      return
    }
    onFile(picked)
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-sm font-medium text-secondary-800">{label}</label>
        {previewUrl && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center text-xs font-medium text-red-600 hover:text-red-700"
          >
            <Trash2 className="mr-1 h-3 w-3" />
            Remove
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-secondary-500">{hint}</p>
      <label
        className={`relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed transition ${
          compact ? 'h-28' : 'h-40'
        } ${
          dragOver
            ? 'border-primary-400 bg-primary-50'
            : previewUrl
              ? 'border-secondary-200 bg-white'
              : 'border-secondary-300 bg-secondary-50 hover:border-primary-300 hover:bg-primary-50/40'
        }`}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          takeFile(e.dataTransfer.files?.[0])
        }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain p-4" />
        ) : (
          <div className="px-4 text-center">
            <Upload className="mx-auto mb-2 h-6 w-6 text-secondary-400" />
            <p className="text-sm text-secondary-600">
              <span className="font-semibold text-primary-600">Click to upload</span> or drag and drop
            </p>
            <p className="mt-1 text-xs text-secondary-400">PNG, SVG, JPG, ICO · max 5MB</p>
          </div>
        )}
        <input
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            takeFile(e.target.files?.[0])
            e.target.value = ''
          }}
        />
      </label>
      {file && <p className="mt-2 truncate text-xs text-secondary-500">{file.name}</p>}
    </div>
  )
}

function ColorField({ label, value, onChange }) {
  const safe = HEX.test(value) ? value : DEFAULT_PRIMARY
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-secondary-800">{label}</label>
      <div className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-white p-2">
        <label className="relative h-11 w-11 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-secondary-200 shadow-sm">
          <span className="absolute inset-0" style={{ backgroundColor: safe }} />
          <input
            type="color"
            value={safe}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="border-0 font-mono uppercase shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  )
}

function LivePreview({ appName, primaryColor, secondaryColor, logoUrl }) {
  const logo = logoUrl || enginuoLogo
  const primary = HEX.test(primaryColor) ? primaryColor : DEFAULT_PRIMARY
  const secondary = HEX.test(secondaryColor) ? secondaryColor : DEFAULT_SECONDARY

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl border border-secondary-200 bg-secondary-50">
        <div className="flex items-center justify-between border-b border-secondary-200 bg-white px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-400">Sidebar</p>
          <Monitor className="h-3.5 w-3.5 text-secondary-400" />
        </div>
        <div className="flex min-h-[220px]">
          <div className="w-[42%] border-r border-secondary-200 bg-white">
            <div className="flex h-12 items-center justify-center border-b border-secondary-200 px-3">
              <img src={logo} alt="" className="h-8 w-auto max-w-[120px] object-contain" />
            </div>
            <div className="space-y-1 p-2">
              {['Dashboard', 'Organizations', 'Branding'].map((item, i) => (
                <div
                  key={item}
                  className={`rounded-md px-2 py-1.5 text-[11px] font-medium ${
                    i === 2 ? 'text-white' : 'text-secondary-500'
                  }`}
                  style={i === 2 ? { backgroundColor: primary } : undefined}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-1 items-center justify-center p-4">
            <div className="w-full max-w-[160px] rounded-lg bg-white p-3 shadow-sm">
              <div className="h-2 w-16 rounded bg-secondary-100" />
              <div className="mt-2 h-2 w-24 rounded bg-secondary-100" />
              <div className="mt-3 h-16 rounded bg-secondary-50" />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-secondary-200 bg-gradient-to-br from-primary-50 to-secondary-100">
        <div className="flex items-center justify-between border-b border-secondary-200/70 bg-white/70 px-3 py-2 backdrop-blur">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary-400">Sign in</p>
          <Globe className="h-3.5 w-3.5 text-secondary-400" />
        </div>
        <div className="flex justify-center p-5">
          <div className="w-full max-w-[220px] rounded-xl bg-white p-4 text-center shadow-md">
            <img src={logo} alt="" className="mx-auto h-8 w-auto max-w-[140px] object-contain" />
            <p className="mt-3 text-sm font-semibold text-secondary-900">Sign in to {appName || 'Product Hub'}</p>
            <div className="mt-3 h-8 rounded-md border border-secondary-200 bg-secondary-50" />
            <div className="mt-2 h-8 rounded-md text-[11px] font-semibold leading-8 text-white" style={{ backgroundColor: primary }}>
              Continue
            </div>
            <div className="mt-2 h-1.5 w-16 mx-auto rounded" style={{ backgroundColor: secondary }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Branding() {
  const queryClient = useQueryClient()
  const { branding, isLoading } = useHubBranding()
  const [activeTab, setActiveTab] = useState('appearance')
  const [appName, setAppName] = useState('Product Hub')
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY)
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY)
  const [logoFile, setLogoFile] = useState(null)
  const [faviconFile, setFaviconFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState('')
  const [faviconPreview, setFaviconPreview] = useState('')
  const [clearLogo, setClearLogo] = useState(false)
  const [clearFavicon, setClearFavicon] = useState(false)

  const applySavedAppearance = () => {
    if (!branding) return
    setAppName(branding.app_name || 'Product Hub')
    setPrimaryColor(normalizeHex(branding.primary_color, DEFAULT_PRIMARY))
    setSecondaryColor(normalizeHex(branding.secondary_color, DEFAULT_SECONDARY))
    setLogoPreview(branding.logo_url || '')
    setFaviconPreview(branding.favicon_url || '')
    setLogoFile(null)
    setFaviconFile(null)
    setClearLogo(false)
    setClearFavicon(false)
  }

  useEffect(() => {
    applySavedAppearance()
  }, [branding])

  const previewFile = (file, setter) => {
    if (!file) {
      setter('')
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => setter(reader.result)
    reader.readAsDataURL(file)
  }

  const isDirty = useMemo(() => {
    if (!branding) return false
    return (
      appName.trim() !== (branding.app_name || 'Product Hub') ||
      normalizeHex(primaryColor, DEFAULT_PRIMARY) !== normalizeHex(branding.primary_color, DEFAULT_PRIMARY) ||
      normalizeHex(secondaryColor, DEFAULT_SECONDARY) !== normalizeHex(branding.secondary_color, DEFAULT_SECONDARY) ||
      !!logoFile ||
      !!faviconFile ||
      clearLogo ||
      clearFavicon
    )
  }, [appName, branding, clearFavicon, clearLogo, faviconFile, logoFile, primaryColor, secondaryColor])

  const mutation = useMutation((formData) => hubBrandingAPI.update(formData), {
    onSuccess: () => {
      queryClient.invalidateQueries([HUB_BRANDING_QUERY])
      toast.success('Branding saved')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to save branding')
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!appName.trim()) {
      toast.error('App name is required')
      return
    }
    if (!HEX.test(primaryColor) || !HEX.test(secondaryColor)) {
      toast.error('Colors must be hex values like #3B82F6')
      return
    }
    const formData = new FormData()
    formData.append('app_name', appName.trim())
    formData.append('primary_color', primaryColor)
    formData.append('secondary_color', secondaryColor)
    if (logoFile) formData.append('logo', logoFile)
    if (faviconFile) formData.append('favicon', faviconFile)
    if (clearLogo && !logoFile) formData.append('clear_logo', 'true')
    if (clearFavicon && !faviconFile) formData.append('clear_favicon', 'true')
    mutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const tabs = [
    { id: 'appearance', name: 'Appearance', icon: Palette },
    { id: 'email', name: 'Email', icon: Mail }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Branding</h1>
        <p className="mt-1 text-secondary-600">
          Customize how Product Hub looks in the app and in outgoing emails.
        </p>
      </div>

      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center border-b-2 px-1 py-2 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:border-secondary-300 hover:text-secondary-700'
                }`}
              >
                <Icon className="mr-2 h-4 w-4" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      <div className={activeTab === 'email' ? 'block' : 'hidden'}>
        <EmailBrandingTab branding={branding} active={activeTab === 'email'} />
      </div>
      <div className={activeTab === 'appearance' ? 'block' : 'hidden'}>
    <form onSubmit={handleSubmit} className="space-y-6 pb-24">
      <div className="flex justify-end">
        {isDirty && (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
            Unsaved changes
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Type className="mr-2 h-5 w-5 text-primary-600" />
                Identity
              </CardTitle>
              <CardDescription>Name shown in the browser tab and on the sign-in screen.</CardDescription>
            </CardHeader>
            <CardContent>
              <label htmlFor="app_name" className="mb-2 block text-sm font-medium text-secondary-800">
                App name
              </label>
              <Input
                id="app_name"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                maxLength={100}
                required
                placeholder="Product Hub"
              />
              <p className="mt-2 text-xs text-secondary-400">{appName.trim().length}/100</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Palette className="mr-2 h-5 w-5 text-primary-600" />
                Colors
              </CardTitle>
              <CardDescription>Used on buttons, active nav, and accents.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ColorField label="Primary" value={primaryColor} onChange={setPrimaryColor} />
                <ColorField label="Secondary" value={secondaryColor} onChange={setSecondaryColor} />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-full px-3 py-1 text-xs font-medium text-white" style={{ backgroundColor: HEX.test(primaryColor) ? primaryColor : DEFAULT_PRIMARY }}>
                  Primary
                </span>
                <span className="rounded-full px-3 py-1 text-xs font-medium text-white" style={{ backgroundColor: HEX.test(secondaryColor) ? secondaryColor : DEFAULT_SECONDARY }}>
                  Secondary
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <ImageIcon className="mr-2 h-5 w-5 text-primary-600" />
                Assets
              </CardTitle>
              <CardDescription>Logo appears in the sidebar and login. Favicon is the browser tab icon.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <DropZone
                  label="Logo"
                  hint="Recommended transparent PNG or SVG."
                  previewUrl={logoPreview}
                  file={logoFile}
                  onFile={(file) => {
                    setLogoFile(file)
                    setClearLogo(false)
                    previewFile(file, setLogoPreview)
                  }}
                  onClear={() => {
                    setLogoFile(null)
                    setLogoPreview('')
                    setClearLogo(true)
                  }}
                />
                <DropZone
                  label="Favicon"
                  hint="Square 32×32 or 64×64 works best."
                  previewUrl={faviconPreview}
                  file={faviconFile}
                  compact
                  onFile={(file) => {
                    setFaviconFile(file)
                    setClearFavicon(false)
                    previewFile(file, setFaviconPreview)
                  }}
                  onClear={() => {
                    setFaviconFile(null)
                    setFaviconPreview('')
                    setClearFavicon(true)
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="xl:sticky xl:top-6 h-fit">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Live preview</CardTitle>
              <CardDescription>How this branding will appear in the product.</CardDescription>
            </CardHeader>
            <CardContent>
              <LivePreview
                appName={appName.trim() || 'Product Hub'}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                logoUrl={logoPreview}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className={`fixed inset-x-0 bottom-0 z-20 border-t border-secondary-200 bg-white/95 backdrop-blur lg:left-64 ${activeTab === 'appearance' ? '' : 'hidden'}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <p className="hidden text-sm text-secondary-500 sm:block">
            {isDirty ? 'Save to apply these changes across Product Hub.' : 'Your branding is up to date.'}
          </p>
          <div className="ml-auto flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={applySavedAppearance}
              disabled={mutation.isLoading || !isDirty}
            >
              Discard
            </Button>
            <Button type="submit" loading={mutation.isLoading} disabled={mutation.isLoading || !isDirty}>
              {!mutation.isLoading && <Check className="mr-2 h-4 w-4" />}
              Save branding
            </Button>
          </div>
        </div>
      </div>
    </form>
      </div>
    </div>
  )
}
